import { keyframes, styled } from "goober";
import { type ReactNode, useState } from "react";
import { rootDomain } from "../../../shared/connectors/urlDomain";
import type { CustomDomainSetup } from "../../../shared/generatedDrizzleSchema";
import { CopyableDetailField } from "../Settings/CopyableDetailField";
import { DetailLabel, DetailList, DetailRow } from "../Settings/shared";
import { Skeleton } from "../Skeleton";
import { DomainStatusPill } from "./DomainStatusPill";
import { Text } from "../Text";

const purposeLabel = {
  ownership: "Domain ownership",
  routing: "Routing",
  certificate: "HTTPS certificate",
};

const badgeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const BadgeIn = styled("span")`
  display: inline-flex;
  &[data-enter="true"] {
    animation: ${badgeIn} var(--motion-base) var(--ease-out);
  }
`;

export function RecordHeading({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children?: ReactNode;
}) {
  return (
    <DetailRow style={{ minHeight: 44 }}>
      <DetailLabel
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "baseline",
          gap: 6,
        }}
      >
        <Text variant="text-small-md">{title}</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {subtitle}
        </Text>
      </DetailLabel>
      {children}
    </DetailRow>
  );
}

const PLACEHOLDER_RECORDS = [
  { purpose: "ownership", type: "TXT" },
  { purpose: "routing", type: "CNAME" },
  { purpose: "certificate", type: "CNAME" },
] as const;

function LoadingField() {
  return (
    <DetailRow aria-hidden>
      <DetailLabel style={{ flex: 1, gap: 8 }}>
        <Skeleton style={{ width: 48 }} />
        <Skeleton style={{ height: 32 }} />
      </DetailLabel>
    </DetailRow>
  );
}

export function DnsRecordList({ setup }: { setup: CustomDomainSetup }) {
  const records = setup.dnsRecords;
  const zone = rootDomain(setup.domain);
  // Badges only animate in when records arrive while the list is open, not on every open.
  const [enter] = useState(!records.length);
  if (!records.length)
    return PLACEHOLDER_RECORDS.map(({ purpose, type }) => (
      <DetailList key={purpose} aria-busy>
        <RecordHeading
          title={purposeLabel[purpose]}
          subtitle={`${type} record`}
        />
        <LoadingField />
        <LoadingField />
      </DetailList>
    ));
  return records.map((record) => (
    <DetailList key={record.purpose}>
      <RecordHeading
        title={purposeLabel[record.purpose]}
        subtitle={`${record.type} record`}
      >
        <BadgeIn data-enter={enter}>
          <DomainStatusPill setup={setup} record={record} />
        </BadgeIn>
      </RecordHeading>
      <CopyableDetailField
        label="Name"
        value={
          record.name === zone ? "@" : record.name.slice(0, -zone.length - 1)
        }
      />
      <CopyableDetailField label="Value" value={record.value} />
    </DetailList>
  ));
}
